import { useEffect, useState } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { useCustomers } from '../../hooks/useCustomers';
import { EditCustomerModal } from './EditCustomerModal';
import {
  ArrowLeft, ChevronLeft, ChevronRight, MapPin, Phone, Mail, Calendar,
  DollarSign, ShoppingBag, Star, Loader2, AlertCircle, Plus, Receipt,
  Send, RefreshCw, CreditCard, X, Printer,
} from 'lucide-react';
import { customersService, CustomerTransaction, CreateTransactionDto, TransactionType } from '../../services/customers.service';
import { TransactionReceiptModal } from './TransactionsPage';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { toast } from 'sonner';

interface CustomerDetailsPageProps {
  customerId: string;
  onNavigate?: (page: string) => void;
  onBack: () => void;
  onEdit: (customerId: string) => void;
}

const TIER_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  BRONZE:   { label: 'Bronze',   color: 'bg-orange-50 text-orange-700', icon: '🥉' },
  SILVER:   { label: 'Silver',   color: 'bg-gray-100 text-gray-700', icon: '🥈' },
  GOLD:     { label: 'Gold',     color: 'bg-yellow-50 text-yellow-700', icon: '🥇' },
  PLATINUM: { label: 'Platinum', color: 'bg-indigo-50 text-indigo-700', icon: '💎' },
};

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'bg-green-50 text-green-700',
  PENDING:   'bg-yellow-50 text-yellow-700',
  CANCELLED: 'bg-red-50 text-red-700',
  PREPARING: 'bg-blue-50 text-blue-700',
  READY:     'bg-purple-50 text-purple-700',
};

const TX_TYPE_STYLES: Record<string, string> = {
  PAYMENT: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
  REFUND:  'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
  CREDIT:  'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
  CHARGE:  'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400',
};

export function CustomerDetailsPage({ customerId, onNavigate, onBack }: CustomerDetailsPageProps) {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') return window.innerWidth >= 768;
    return true;
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [transactions, setTransactions] = useState<CustomerTransaction[]>([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const [showTxModal, setShowTxModal] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState<string | null>(null);
  const [printReceiptTx, setPrintReceiptTx] = useState<CustomerTransaction | null>(null);
  const [txForm, setTxForm] = useState<{
    type: TransactionType; amount: string; description: string; notes: string; transactionDate: string;
  }>({
    type: 'PAYMENT',
    amount: '',
    description: '',
    notes: '',
    transactionDate: new Date().toISOString().slice(0, 10),
  });
  const [savingTx, setSavingTx] = useState(false);

  const isSubscriptionBased = localStorage.getItem('isSubscriptionBased') === 'true';

  const { customers: allCustomers, selectedCustomer: customer, loading, error, fetchCustomer } = useCustomers();
  const { restaurantName, restaurantPhone, restaurantEmail, currentBranch } = useRestaurant();
  const restaurantAddress = currentBranch?.address || localStorage.getItem('restaurantAddress') || undefined;

  // Per-customer subscription flag driven by their type
  const customerIsSubscriptionBased = customer?.customerType?.isSubscriptionBased ?? isSubscriptionBased;
  const { formatCurrency } = useCurrency();

  // Fetch the specific customer on mount / when ID changes
  useEffect(() => {
    fetchCustomer(customerId);
  }, [customerId]);

  // Load transactions for all customers
  useEffect(() => {
    setLoadingTx(true);
    customersService.getTransactions(customerId)
      .then((r) => { if (r.success && r.data) setTransactions(r.data); })
      .catch(() => {})
      .finally(() => setLoadingTx(false));
  }, [customerId]);

  const handleSaveTx = async () => {
    if (!txForm.amount || !txForm.description) {
      toast.error('Amount and description are required.');
      return;
    }
    setSavingTx(true);
    try {
      const dto: CreateTransactionDto = {
        type: txForm.type,
        amount: parseFloat(txForm.amount),
        description: txForm.description,
        notes: txForm.notes || undefined,
        transactionDate: txForm.transactionDate ? new Date(txForm.transactionDate).toISOString() : undefined,
      };
      const res = await customersService.createTransaction(customerId, dto);
      if (res.success && res.data) {
        setTransactions((prev) => [res.data!, ...prev]);
        setShowTxModal(false);
        setTxForm({ type: 'PAYMENT', amount: '', description: '', notes: '', transactionDate: new Date().toISOString().slice(0, 10) });
        toast.success('Transaction recorded successfully');
      }
    } catch {
      toast.error('Failed to record transaction');
    } finally {
      setSavingTx(false);
    }
  };

  const handleEmailReceipt = async (txId: string, email: string) => {
    if (!email) { toast.error('This customer has no email on file.'); return; }
    setSendingReceipt(txId);
    try {
      await customersService.emailTransactionReceipt(customerId, txId);
      toast.success('Receipt sent to ' + email);
    } catch {
      toast.error('Failed to send receipt');
    } finally {
      setSendingReceipt(null);
    }
  };

  // Navigation within the loaded customer list
  const currentIndex = allCustomers.findIndex((c) => c.id === customerId);
  const handlePrevious = () => {
    if (currentIndex > 0) onNavigate?.(`customer-details-${allCustomers[currentIndex - 1].id}`);
  };
  const handleNext = () => {
    if (currentIndex < allCustomers.length - 1) onNavigate?.(`customer-details-${allCustomers[currentIndex + 1].id}`);
  };

  const handleSaveEdit = () => {
    setIsEditModalOpen(false);
    fetchCustomer(customerId);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(!sidebarOpen)} activePage="customers" onNavigate={onNavigate} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading customer…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error / not found
  if (error || !customer) {
    return (
      <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(!sidebarOpen)} activePage="customers" onNavigate={onNavigate} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {error || 'Customer not found'}
              </h2>
              <button onClick={onBack} className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                ← Back to Customers
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const fullName = `${customer.firstName} ${customer.lastName}`;
  const initials = `${customer.firstName[0] ?? ''}${customer.lastName[0] ?? ''}`;
  const tier = TIER_CONFIG[customer.membershipTier] || TIER_CONFIG.BRONZE;
  const orders = customer.orders || [];
  const totalSpent = parseFloat(customer.totalSpent as any || '0');

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="customers"
        onNavigate={onNavigate}
      />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onBack}
                  className="p-2 hover:bg-white dark:hover:bg-gray-800 rounded-lg transition-colors border border-gray-200 dark:border-gray-700"
                >
                  <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customer Details</h1>
                  <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">View and manage customer information</p>
                </div>
              </div>

              {/* Prev / Next navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevious}
                  disabled={currentIndex <= 0}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentIndex >= allCustomers.length - 1}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Customer Info Card */}
              <div className="lg:col-span-1 space-y-6">
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Customer Information</h2>
                    <button
                      onClick={() => setIsEditModalOpen(true)}
                      className="px-4 py-2 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      Edit
                    </button>
                  </div>

                  {/* Avatar */}
                  <div className="text-center mb-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <span className="text-3xl font-bold text-white">{initials}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">{fullName}</h3>
                    {customer.customerNumber && (
                      <div className="flex justify-center mb-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono font-bold bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-700">
                          #{customer.customerNumber}
                        </span>
                      </div>
                    )}
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${tier.color}`}>
                      {tier.icon} {tier.label}
                    </span>
                  </div>

                  {/* Contact Details */}
                  <div className="space-y-4">
                    {(customer.city || customer.state || customer.address) && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5">Address</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {[customer.address, customer.city, customer.state, customer.zipCode].filter(Boolean).join(', ')}
                          </p>
                        </div>
                      </div>
                    )}
                    {customer.phone && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5">Phone</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">{customer.phone}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5">Email</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{customer.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-gray-900 dark:text-white mb-0.5">Join Date</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {new Date(customer.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {customer.notes && (
                      <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
                        <p className="text-xs font-semibold text-gray-900 dark:text-white mb-1">Notes</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{customer.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Subscription Info Card */}
                {customerIsSubscriptionBased && (
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <RefreshCw className="w-4 h-4 text-indigo-500" />
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">Subscription</h2>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Type</span>
                        <span className="text-xs font-medium text-gray-900 dark:text-white">{customer.customerType?.name || 'Unknown'}</span>
                      </div>
                      {customer.subscriptionPlan && (
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Plan</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">{customer.subscriptionPlan}</span>
                        </div>
                      )}
                      {customer.subscriptionFrequency && (
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Frequency</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">{customer.subscriptionFrequency}</span>
                        </div>
                      )}
                      {customer.subscriptionStartDate && (
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Start Date</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {new Date(customer.subscriptionStartDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {customer.subscriptionEndDate && (
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">End Date</span>
                          <span className={`text-xs font-medium ${
                            new Date(customer.subscriptionEndDate) < new Date()
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-gray-900 dark:text-white'
                          }`}>
                            {new Date(customer.subscriptionEndDate).toLocaleDateString()}
                            {new Date(customer.subscriptionEndDate) < new Date() && ' — Expired'}
                          </span>
                        </div>
                      )}
                      {customer.nextPaymentDate && (
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Next Payment</span>
                          <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">
                            {new Date(customer.nextPaymentDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Loyalty Points Card */}
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-5 h-5 text-yellow-300" />
                    <p className="text-sm font-semibold">Loyalty Points</p>
                  </div>
                  <p className="text-3xl font-bold mb-1">{customer.loyaltyPoints.toLocaleString()}</p>
                  <p className="text-xs text-indigo-200">points available</p>
                </div>
              </div>

              {/* Right: Stats + Order History */}
              <div className="lg:col-span-2 space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-xl">
                        <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Spent</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">{formatCurrency(totalSpent)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
                        <ShoppingBag className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Orders</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {customer._count?.orders ?? customer.totalOrders ?? orders.length}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-xl">
                        <Star className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Avg Order Value</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {(customer._count?.orders ?? customer.totalOrders ?? orders.length) > 0
                            ? formatCurrency(totalSpent / ((customer._count?.orders ?? customer.totalOrders ?? orders.length) || 1))
                            : formatCurrency(0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order History */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
                  <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Orders</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          {['Order ID', 'Date', 'Items', 'Amount', 'Status'].map((h) => (
                            <th key={h} className="text-left px-6 py-3">
                              <span className="text-xs font-semibold text-gray-900 dark:text-white">{h}</span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {orders.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                              No orders found for this customer
                            </td>
                          </tr>
                        ) : (
                          orders.map((order: any) => {
                            const statusColor = STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-700';
                            const total = parseFloat(order.total as any || '0');
                            return (
                              <tr key={order.id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                <td className="px-6 py-3">
                                  <button
                                    onClick={() => onNavigate?.(`order-details-${order.id}`)}
                                    className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                                  >
                                    {order.orderNumber || order.id.slice(0, 8)}
                                  </button>
                                </td>
                                <td className="px-6 py-3 text-xs text-gray-600 dark:text-gray-400">
                                  {new Date(order.orderDate || order.createdAt).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-3 text-xs text-gray-600 dark:text-gray-400">
                                  {order.items?.length ?? 0} {order.items?.length === 1 ? 'item' : 'items'}
                                </td>
                                <td className="px-6 py-3 text-xs font-semibold text-gray-900 dark:text-white">
                                  {formatCurrency(total)}
                                </td>
                                <td className="px-6 py-3">
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                    {order.status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Transactions Panel */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-indigo-500" />
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Transactions</h2>
                      </div>
                      <button
                        onClick={() => setShowTxModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" /> Record Transaction
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      {loadingTx ? (
                        <div className="flex items-center justify-center py-10">
                          <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                        </div>
                      ) : transactions.length === 0 ? (
                        <div className="py-10 text-center">
                          <Receipt className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">No transactions yet</p>
                        </div>
                      ) : (
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-100 dark:border-gray-800">
                                {['Receipt #', 'Date', 'Type', 'Amount', 'Description', ''].map((h) => (
                                <th key={h} className="text-left px-6 py-3">
                                  <span className="text-xs font-semibold text-gray-900 dark:text-white">{h}</span>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map((tx) => (
                              <tr key={tx.id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="px-6 py-3 text-xs font-mono text-gray-600 dark:text-gray-400">{tx.receiptNumber}</td>
                                <td className="px-6 py-3 text-xs text-gray-600 dark:text-gray-400">
                                  {new Date(tx.transactionDate).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-3">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TX_TYPE_STYLES[tx.type] || ''}` }>
                                    {tx.type}
                                  </span>
                                </td>
                                <td className="px-6 py-3 text-xs font-semibold text-gray-900 dark:text-white">
                                  {formatCurrency(tx.amount)}
                                </td>
                                <td className="px-6 py-3 text-xs text-gray-600 dark:text-gray-400 max-w-[200px] truncate">
                                  {tx.description}
                                </td>
                                <td className="px-6 py-3">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleEmailReceipt(tx.id, customer.email)}
                                      disabled={sendingReceipt === tx.id}
                                      title="Email receipt"
                                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                      {sendingReceipt === tx.id
                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        : <Send className="w-3.5 h-3.5" />
                                      }
                                      Email
                                    </button>
                                    <button
                                      onClick={() => setPrintReceiptTx(tx)}
                                      title="Print receipt"
                                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                    >
                                      <Printer className="w-3.5 h-3.5" />
                                      Print
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Record Transaction Modal */}
      {showTxModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Record Transaction</h3>
              <button onClick={() => setShowTxModal(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Type</label>
                <select
                  value={txForm.type}
                  onChange={(e) => setTxForm((f) => ({ ...f, type: e.target.value as TransactionType }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="PAYMENT">Payment</option>
                  <option value="REFUND">Refund</option>
                  <option value="CREDIT">Credit</option>
                  <option value="CHARGE">Charge</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={txForm.amount}
                  onChange={(e) => setTxForm((f) => ({ ...f, amount: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Description *</label>
                <input
                  type="text"
                  value={txForm.description}
                  onChange={(e) => setTxForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. Monthly subscription payment"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Notes (optional)</label>
                <textarea
                  value={txForm.notes}
                  onChange={(e) => setTxForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Date</label>
                <input
                  type="date"
                  value={txForm.transactionDate}
                  onChange={(e) => setTxForm((f) => ({ ...f, transactionDate: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={() => setShowTxModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTx}
                disabled={savingTx}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60"
              >
                {savingTx && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save Transaction
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {isEditModalOpen && (
        <EditCustomerModal
          customerId={customerId}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Print Receipt Modal */}
      {printReceiptTx && customer && (
        <TransactionReceiptModal
          transaction={printReceiptTx}
          customer={customer}
          businessName={restaurantName}
          businessAddress={restaurantAddress}
          businessPhone={restaurantPhone}
          businessEmail={restaurantEmail}
          formatCurrency={formatCurrency}
          onClose={() => setPrintReceiptTx(null)}
        />
      )}
    </div>
  );
}
